import { useState } from 'react'
import { createTrustAccountEntry, deleteTrustAccountEntry, updateTrustAccountEntry } from 'services'
import { Modal } from 'stories/components'
import { solveDecimalJavascriptSum } from 'utils'

import { confirmSufficientFunds } from './sufficientFunds'
import type { ITrustAccount, ITrustAccountLedgerResponse, ITrustAccountLedgerRow } from './types'

/**
 * Type and direction are one choice, not two: a "Deposit" that takes money out would be a
 * contradiction the ledger cannot resolve, so the option carries both.
 */
const TYPES: Array<{ value: string; title: string; isDeposit: boolean }> = [
  { value: 'Deposit', title: 'Deposit (money in)', isDeposit: true },
  { value: 'Disbursement', title: 'Disbursement (money out)', isDeposit: false },
  { value: 'Adjustment', title: 'Adjustment - increase', isDeposit: true },
  { value: 'Adjustment', title: 'Adjustment - decrease', isDeposit: false },
]

const typeKey = (type: string, isDeposit: boolean) => `${type}|${isDeposit ? 'in' : 'out'}`

/**
 * Add or edit an account-level trust transaction — money held for a party that did not come
 * through a loan. A transfer is not edited here: both halves have to move together, so it is
 * deleted and entered again.
 */
export default function TrustAccountEntryModal({
  accounts,
  row,
  defaults,
  balances,
  onClose,
}: {
  accounts: ITrustAccount[]
  row: ITrustAccountLedgerRow | null
  defaults?: { trustAccountId?: number; partyType?: string; partyId?: number }
  /**
   * What each account holds, from the ledger grid beside this modal. Left out, a withdrawal is
   * written without the funds warning rather than against a balance that might be wrong.
   */
  balances?: ITrustAccountLedgerResponse['totals']
  onClose: (changed: boolean) => void
}) {
  const isTransfer = !!row?.transferGroup
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [values, setValues] = useState({
    trustAccountId: row?.trustAccountId || defaults?.trustAccountId || accounts[0]?.id || 0,
    date: row?.date || new Date().toISOString().slice(0, 10),
    type: row?.type || 'Deposit',
    reference: row?.reference || '',
    fromWhom: row?.fromWhom || '',
    memo: row?.memo || '',
    amount: row ? String(row.deposit || row.payment || '') : '',
    isDeposit: row ? row.deposit > 0 : true,
  })

  const set = (key: string, value: any) => setValues((prev) => ({ ...prev, [key]: value }))

  /**
   * Warn before taking more out of an account than it holds. A transfer between accounts is
   * entered on its own modal, so the only withdrawal that reaches here is this one entry; what the
   * row being edited already takes out is added back so the check measures the change.
   */
  const confirmFunds = async (trustAccountId: number, payment: number) => {
    const account = balances?.find((item) => item.trustAccountId === trustAccountId)
    if (!account) return true
    const editedWithdrawal = row ? solveDecimalJavascriptSum([row.payment, row.fees, -1 * row.deposit]) : 0
    return confirmSufficientFunds(payment, solveDecimalJavascriptSum([account.balance, editedWithdrawal]), account.name)
  }

  const onSubmit = async () => {
    setError('')
    const amount = Number(String(values.amount).replace(/,/g, ''))
    if (!amount) return setError('Enter an amount')
    const payload = {
      trustAccountId: Number(values.trustAccountId),
      partyType: row?.partyType || defaults?.partyType || 'Company',
      partyId: Number(row?.partyId ?? defaults?.partyId ?? 0),
      date: values.date,
      type: values.type,
      reference: values.reference,
      fromWhom: values.fromWhom,
      memo: values.memo,
      deposit: values.isDeposit ? amount : 0,
      payment: values.isDeposit ? 0 : amount,
    }
    if (!(await confirmFunds(payload.trustAccountId, payload.payment))) return

    setLoading(true)
    try {
      if (row) await updateTrustAccountEntry(row.id, payload)
      else await createTrustAccountEntry(payload)
      onClose(true)
    } catch (err: any) {
      setError(err?.message || 'Could not save the entry')
    } finally {
      setLoading(false)
    }
  }

  const onDelete = async () => {
    if (!row) return
    setLoading(true)
    try {
      await deleteTrustAccountEntry(row.id)
      onClose(true)
    } catch (err: any) {
      setError(err?.message || 'Could not delete the entry')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={row ? 'Update Trust Transaction' : 'Add Trust Transaction'}
      titleOkay={isTransfer ? '' : 'Save'}
      loading={loading}
      onClose={() => onClose(false)}
      onOk={onSubmit}
      isOpen
    >
      <div className="text-gray-900 w-144 grid grid-cols-2 gap-4 mb-3 mt-2">
        <div className="col-span-2">
          <div className="text-[13px] text-gray-500 mb-1">Trust Account</div>
          <select
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.trustAccountId}
            disabled={isTransfer}
            onChange={(e) => set('trustAccountId', e.target.value)}
          >
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Date</div>
          <input
            type="date"
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.date}
            disabled={isTransfer}
            onChange={(e) => set('date', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Type</div>
          <select
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={typeKey(values.type, values.isDeposit)}
            disabled={isTransfer}
            onChange={(e) => {
              const chosen = TYPES.find((type) => typeKey(type.value, type.isDeposit) === e.target.value)
              if (!chosen) return
              setValues((prev) => ({ ...prev, type: chosen.value, isDeposit: chosen.isDeposit }))
            }}
          >
            {TYPES.map((type) => (
              <option key={typeKey(type.value, type.isDeposit)} value={typeKey(type.value, type.isDeposit)}>
                {type.title}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Reference</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.reference}
            disabled={isTransfer}
            onChange={(e) => set('reference', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">From Whom Received/Paid</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.fromWhom}
            disabled={isTransfer}
            onChange={(e) => set('fromWhom', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Amount</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.amount}
            disabled={isTransfer}
            onChange={(e) => set('amount', e.target.value)}
          />
        </div>

        <div className="col-span-2">
          <div className="text-[13px] text-gray-500 mb-1">Memo</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.memo}
            disabled={isTransfer}
            onChange={(e) => set('memo', e.target.value)}
          />
        </div>

        {isTransfer && (
          <div className="col-span-2 text-[13px] text-gray-500">
            This is one half of a transfer between accounts. Deleting it removes both halves, so the two accounts
            cannot be left disagreeing.
          </div>
        )}
        {!!error && <div className="col-span-2 text-[13px] text-red-600">{error}</div>}
        {!!row && (
          <div className="col-span-2">
            <button className="text-[13px] text-red-600 hover:underline" onClick={onDelete}>
              Delete this transaction
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}
